import i18n from '@/utils/i18n';
import React, { useCallback, useEffect, useState } from 'react';
import { ErrorState } from '@/experiences/sales-portal/components/error-state';
import { ItemDetailContentData } from '@/experiences/sales-portal/domain/items/blocs/types/item-detail-content';
import { ItemDetailDetailsLoadingSkeleton } from '@/experiences/sales-portal/domain/items/components/details-tab/details-tab-loading-skeleton';
import { ItemDetailEmptyState } from '@/experiences/sales-portal/domain/items/components/item-detail-empty-state';
import { ItemDetailGeneralLoadingSkeleton } from '@/experiences/sales-portal/domain/items/components/general-tab/general-tab-loading-skeleton';
import { ItemDetailTabBar } from '@/experiences/sales-portal/domain/items/components/item-detail-tab-bar';
import { ItemDetailTabKey } from '@/enums';
import { ItemImage } from '@usepepper/ui/atoms/item-image';
import { OrderEntryMobileDetailsTab } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-mobile-details-tab';
import { OrderEntryMobileGeneralTab } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-mobile-general-tab';
import { OrderEntryMobileKeyboardDoneBar } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-mobile-keyboard-done-bar';
import { OrderEntryMobilePriceEditFields } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-mobile-price-edit-fields';
import { OrderEntryTableRowData } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-table-transformers';
import { PriceEditor } from '@/domain/pricing/services/price-editor';
import {
  PushView,
  ScrollView,
  Typography,
  XStack,
  YStack,
} from '@usepepper/sprout-kit-ui';
import { Spacing } from '@usepepper/sprout-kit';
import { useItemDetailContentLoader } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/loaders/use-item-detail-content-loader';
import { usePricingStore } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/stores/pricing-store';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/contexts/theme-context';

interface OrderEntryMobileItemDetailProps {
  canSetManualOverrideExpiration: boolean;
  deliveryDate: string;
  isGrossProfitHidden: boolean;
  isPriceEditingEnabled: boolean;
  onClose: () => void;
  onSavePriceEdit: (
    editor: PriceEditor,
    variantPackUUID: string,
  ) => Promise<boolean>;
  open: boolean;
  restaurantTimezone: string;
  restaurantUUID: string;
  row: OrderEntryTableRowData | undefined;
  showPriceOverrideInfo: boolean;
  supplierUUID: string;
  variantPackUUID: string;
}

const isDetailDataEmpty = ({
  displayConfigs,
  purchaseHistory,
  relatedVariantPacks,
  saltInfo,
}: ItemDetailContentData): boolean =>
  purchaseHistory.length === 0 &&
  relatedVariantPacks.length === 0 &&
  saltInfo === null &&
  displayConfigs.length === 0;

export const OrderEntryMobileItemDetail: React.FC<
  OrderEntryMobileItemDetailProps
> = ({
  canSetManualOverrideExpiration,
  deliveryDate,
  isGrossProfitHidden,
  isPriceEditingEnabled,
  onClose,
  onSavePriceEdit,
  open,
  restaurantTimezone,
  restaurantUUID,
  row,
  showPriceOverrideInfo,
  supplierUUID,
  variantPackUUID,
}) => {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const editor = usePricingStore.getState().editors.get(variantPackUUID);
  const isItemPriceEditable =
    isPriceEditingEnabled && (editor?.isPriceEditingEnabled(true) ?? false);
  const [activeTab, setActiveTab] = useState<ItemDetailTabKey>(
    ItemDetailTabKey.General,
  );
  const [isPriceInputFocused, setIsPriceInputFocused] = useState(false);

  const data = useItemDetailContentLoader({
    deliveryDate,
    priceEditDisplayHeaders: row?.priceEditDisplayHeaders,
    restaurantId: restaurantUUID,
    restaurantTimezone,
    supplierId: supplierUUID,
    variantPackId: variantPackUUID,
  });
  const { error, itemInfo, loading, refetch } = data;
  const liveRow = data.row ?? row;
  const imageUrl = liveRow?.imageUrl ?? itemInfo.imageUrl ?? null;
  const subtitle = [
    liveRow?.packDisplayString ?? itemInfo.packDisplayString,
    liveRow?.externalItemId ?? itemInfo.externalItemId,
  ]
    .filter(Boolean)
    .join(' · ');

  const isSalesRepInfoDisabled = editor?.isSalesRepInfoDisabled() ?? false;
  const scrollContentStyle = {
    flexGrow: 1,
    paddingBottom: insets.bottom + theme.sk.spacing.spaceMedium,
  };

  useEffect(() => {
    setIsPriceInputFocused(false);
    if (open) {
      setActiveTab(ItemDetailTabKey.General);
    }
  }, [open]);

  const handleSelectTab = useCallback(
    (tab: ItemDetailTabKey) => () => setActiveTab(tab),
    [],
  );
  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        onClose();
      }
    },
    [onClose],
  );

  let body: React.ReactNode;
  if (loading) {
    body =
      activeTab === ItemDetailTabKey.Details ? (
        <ItemDetailDetailsLoadingSkeleton />
      ) : (
        <ItemDetailGeneralLoadingSkeleton />
      );
  } else if (isDetailDataEmpty(data)) {
    body = <ItemDetailEmptyState itemName={itemInfo.name} />;
  } else if (activeTab === ItemDetailTabKey.General) {
    body = (
      <OrderEntryMobileGeneralTab
        canSetManualOverrideExpiration={canSetManualOverrideExpiration}
        data={data}
        isGrossProfitHidden={isGrossProfitHidden}
        isSalesRepInfoDisabled={isSalesRepInfoDisabled}
        labels={liveRow?.labels}
        omitPriceAndGrossProfitRows={isItemPriceEditable}
        orderGuideLabels={liveRow?.orderGuideLabels ?? []}
        priceOverrideInfo={liveRow?.priceOverrideInfo}
        showPriceOverrideInfo={showPriceOverrideInfo}
        unitCount={liveRow?.unitCount ?? 1}
      />
    );
  } else {
    body = <OrderEntryMobileDetailsTab data={data} />;
  }

  return (
    <PushView
      aria-label={i18n.t('sales_portal.order_entry.card.view_details_label', {
        name: liveRow?.name ?? itemInfo.name,
      })}
      onOpenChange={handleOpenChange}
      open={open}
    >
      <YStack paddingHorizontal="$16">
        <PushView.Header backLabel={i18n.t('back')} />
      </YStack>
      <PushView.Body padding="$0">
        <ScrollView
          automaticallyAdjustKeyboardInsets
          contentContainerStyle={scrollContentStyle}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <XStack
            alignItems="center"
            gap="$12"
            paddingBottom="$12"
            paddingHorizontal="$24"
            paddingTop="$12"
          >
            <ItemImage
              alt={liveRow?.name ?? itemInfo.name}
              cachePolicy="memory-disk"
              contentFit="cover"
              imageURLs={imageUrl ? [imageUrl] : []}
              size={Spacing.SPACE_4XLARGE}
            />
            <YStack flex={1} gap="$4" minWidth={0}>
              <Typography numberOfLines={2} size="$lg" variant="heading">
                {liveRow?.name ?? itemInfo.name}
              </Typography>
              <Typography
                color={theme.sk.color.text.secondary}
                numberOfLines={1}
                size="$sm"
              >
                {subtitle}
              </Typography>
            </YStack>
          </XStack>
          {error ? (
            <YStack flex={1} justifyContent="center" padding="$24">
              <ErrorState
                description={i18n.t(
                  'sales_portal.order_entry.item_detail.error.description',
                )}
                onPress={() => void refetch()}
                title={i18n.t(
                  'sales_portal.order_entry.item_detail.error.title',
                )}
              />
            </YStack>
          ) : (
            <>
              <ItemDetailTabBar
                activeTab={activeTab}
                onSelect={handleSelectTab}
              />
              <YStack paddingHorizontal="$24" paddingTop="$12">
                {activeTab === ItemDetailTabKey.General &&
                isItemPriceEditable ? (
                  <YStack paddingBottom="$16">
                    <OrderEntryMobilePriceEditFields
                      canSetManualOverrideExpiration={
                        canSetManualOverrideExpiration
                      }
                      isGrossProfitHidden={isGrossProfitHidden}
                      onInputFocusChange={setIsPriceInputFocused}
                      onSave={onSavePriceEdit}
                      priceDisplayString={liveRow?.priceDisplayString ?? ''}
                      priceOverrideInfo={liveRow?.priceOverrideInfo}
                      unitCount={liveRow?.unitCount ?? 1}
                      unitPriceInfo={liveRow?.unitPriceInfo}
                      unitSuffix={liveRow?.unitSuffix}
                      variantPackUUID={variantPackUUID}
                    />
                  </YStack>
                ) : null}
                {body}
              </YStack>
            </>
          )}
        </ScrollView>
      </PushView.Body>
      {isItemPriceEditable ? (
        <OrderEntryMobileKeyboardDoneBar isVisible={isPriceInputFocused} />
      ) : null}
    </PushView>
  );
};
